import type { Media, Shop } from '@prisma/client'
import { XIcon } from 'lucide-react'
import { useCallback, useContext, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { DrawerHeaderLayout } from '~/components/drawer-layout'
import { MediaDeleteDialog } from '~/components/MediaManager/components/MediaDeleteDialog'
import {
  MediaGrid,
  MediaGridEmptyState,
} from '~/components/MediaManager/components/MediaGrid'
import { SelectedMediaBar } from '~/components/MediaManager/components/SelectedMediaBar'
import { useAttachmentManager } from '~/components/MediaManager/hooks/useAttachmentManager'
import { useMediaDeletion } from '~/components/MediaManager/hooks/useMediaDeletion'
import { useMediaFetching } from '~/components/MediaManager/hooks/useMediaFetching'
import { useMediaUpload } from '~/components/MediaManager/hooks/useMediaUpload'
import { InventoryContext, MediaManagerContext } from '~/contexts'
import { useShop } from '~/hooks/data/use-shop'
import { useUnsavedChangesWarning } from '~/hooks/use-unsaved-changes-warning'
import { Drawer, IconButton, Spinner } from '~/ui'

export function MediaManager() {
  const { t } = useTranslation()
  const shop = useShop() as Shop
  const [mediaManagerContext, setMediaManagerContext] =
    useContext(MediaManagerContext)
  const [_, setInventoryContext] = useContext(InventoryContext)

  // Check if single-select mode
  const isSingleSelect = mediaManagerContext.maxSelected === 1

  // Fetch media with pagination
  const { media, isFetching, addMedia, removeMedia, handleScroll } =
    useMediaFetching(shop)

  // Upload handling
  const {
    uploadInputId,
    isUploading,
    uploadError,
    uploadProgress,
    previewMedia,
    handleFileUpload,
    abortFileUpload,
    abortAllUploads,
    getProgressForMedia,
  } = useMediaUpload({
    shopId: shop.id,
    onUploadSuccess: (uploadedMedia) => {
      addMedia(uploadedMedia)
    },
    allowedMediaKinds: mediaManagerContext.allowedMediaKinds,
  })

  // Cleanup uploads on unmount
  useEffect(() => {
    return () => {
      abortAllUploads()
    }
  }, [abortAllUploads])

  // Attachment management
  const {
    attachmentItems,
    placeholderItems,
    isAttachmentsDirty,
    isNewSelection,
    updating,
    handleSelect: handleSelectFromAttachmentManager,
    handleReorder,
    commitChanges,
    returnChanges,
  } = useAttachmentManager({
    shop,
    maxSelected: mediaManagerContext.maxSelected,
    targetType: mediaManagerContext.targetType,
    targetId: mediaManagerContext.targetId ?? undefined,
    initialAttachments: mediaManagerContext.attachments,
    onComplete: (attachments) => {
      setMediaManagerContext((old) => ({ ...old, isOpen: false }))
      mediaManagerContext.onAttachmentsComplete?.(attachments)
    },
    onChange: (attachments) => {
      mediaManagerContext.onAttachmentsChange?.(attachments)
    },
  })

  // Wrap handleSelect to add instant selection for single-select mode
  const handleSelect = useCallback(
    (mediaId: string, media: Media, isSelected: boolean) => {
      if (isSingleSelect && isSelected) {
        // Instant selection for single-select mode
        const attachment = {
          id: undefined,
          media,
          targetId: mediaManagerContext.targetId ?? undefined,
          targetIdByKey: '',
          targetType: mediaManagerContext.targetType,
          disposition: 0,
        }

        // Close immediately and trigger completion
        setMediaManagerContext((old) => ({ ...old, isOpen: false }))
        mediaManagerContext.onAttachmentsComplete?.([attachment])
        return
      }

      // Normal multi-select behavior
      handleSelectFromAttachmentManager(mediaId, media, isSelected)
    },
    [
      isSingleSelect,
      handleSelectFromAttachmentManager,
      mediaManagerContext,
      setMediaManagerContext,
    ]
  )

  // Media deletion
  const {
    mediaToDelete,
    mediaUsage,
    isLoadingUsage,
    deleteError,
    initiateDelete,
    cancelDelete,
    confirmDelete,
  } = useMediaDeletion(shop)

  const handleSave = () => {
    if (!(attachmentItems.length > 0)) {
      setInventoryContext({ hasManagedInventory: false })
    }

    if (mediaManagerContext.intent === 'return_attachments') {
      returnChanges()
      setMediaManagerContext((old) => ({ ...old, isOpen: false }))
    } else if (mediaManagerContext.intent === 'upload_attachments') {
      commitChanges()
    }
  }

  // Unsaved changes warning (disabled for single-select mode)
  const { handleClose: handleCloseWithWarning, ConfirmationModal } =
    useUnsavedChangesWarning({
      hasUnsavedChanges: isSingleSelect ? false : isAttachmentsDirty,
      onSave: handleSave,
      onDiscard: () => {
        setMediaManagerContext((old) => ({ ...old, isOpen: false }))
      },
    })

  // Combined media (preview + fetched)
  const allMedia = useMemo(() => {
    const combined = [...previewMedia, ...media]

    // Filter by allowed media kinds if specified
    if (mediaManagerContext.allowedMediaKinds) {
      return combined.filter((m) =>
        mediaManagerContext.allowedMediaKinds?.includes(m.kind)
      )
    }

    return combined
  }, [previewMedia, media, mediaManagerContext.allowedMediaKinds])

  const selectedMediaIds = useMemo(
    () => new Set(attachmentItems.map((att) => att.media.id)),
    [attachmentItems]
  )

  const handleDeleteMedia = useCallback(
    (mediaId: string) => {
      // Capture media item before deletion for rollback
      const mediaItem = media.find((m) => m.id === mediaId)
      if (!mediaItem) return

      const wasSelected = selectedMediaIds.has(mediaId)

      confirmDelete(
        () => {
          // Optimistic update: remove from list and deselect
          removeMedia(mediaId)
          if (wasSelected) {
            handleSelect(mediaId, mediaItem, false)
          }
        },
        () => {
          // Rollback on error: re-add media and re-select if needed
          addMedia([mediaItem])
          if (wasSelected) {
            handleSelect(mediaId, mediaItem, true)
          }
        }
      )
    },
    [
      media,
      selectedMediaIds,
      confirmDelete,
      removeMedia,
      handleSelect,
      addMedia,
    ]
  )

  const hasMedia = allMedia.length > 0

  return (
    <>
      <MediaDeleteDialog
        isOpen={!!mediaToDelete}
        isLoadingUsage={isLoadingUsage}
        mediaUsage={mediaUsage}
        errorMessage={deleteError}
        onCancel={cancelDelete}
        onConfirm={() => mediaToDelete && handleDeleteMedia(mediaToDelete)}
        onNavigate={() => {
          cancelDelete()
          setMediaManagerContext((old) => ({ ...old, isOpen: false }))
        }}
      />

      <ConfirmationModal />

      <Drawer
        open={mediaManagerContext.isOpen}
        onOpenChange={(open: boolean) => {
          if (!open && mediaToDelete) return
          if (!open) {
            handleCloseWithWarning()
            return
          }
          setMediaManagerContext((old) => ({ ...old, isOpen: open }))
        }}
      >
        <Drawer.Content
          direction="bottom"
          className="text-foreground flex flex-col focus-visible:ring-0"
        >
          {/* Header */}
          <DrawerHeaderLayout className="justify-between">
            <Drawer.Title className="pl-2 text-sm font-medium">
              {t('components.media_manager.select_media_for')}
              <span className="text-foreground/64 ml-1">
                {mediaManagerContext.title}
              </span>
            </Drawer.Title>

            <Drawer.Description className="sr-only">
              {t('action.click_to_add')}
            </Drawer.Description>

            {/* Error alerts */}
            {(uploadError || deleteError) && (
              <div className="z-popover absolute top-1/2 left-1/2 block -translate-x-1/2 -translate-y-1/2 pt-8">
                {/* Errors are displayed within MediaGrid */}
              </div>
            )}

            <IconButton
              variant="ghost"
              size="md"
              aria-label={t('action.close')}
              onClick={handleCloseWithWarning}
            >
              <XIcon />
            </IconButton>
          </DrawerHeaderLayout>

          {/* Body */}
          {isFetching && !hasMedia ? (
            <div className="relative flex h-2/3 w-full items-center justify-center overflow-y-hidden">
              <Spinner size="lg" />
            </div>
          ) : hasMedia ? (
            <div
              className="flex flex-1 flex-col overflow-y-auto"
              onScroll={handleScroll}
            >
              <MediaGrid
                media={allMedia}
                selectedMediaIds={selectedMediaIds}
                uploadInputId={uploadInputId}
                isUploading={isUploading}
                uploadProgress={uploadProgress}
                uploadError={uploadError}
                onSelect={handleSelect}
                onDelete={initiateDelete}
                onFileUpload={handleFileUpload}
                onCancelUpload={abortFileUpload}
                getProgressForMedia={getProgressForMedia}
                allowedMediaKinds={mediaManagerContext.allowedMediaKinds}
              />

              {!isSingleSelect && (
                <SelectedMediaBar
                  attachments={attachmentItems}
                  placeholders={placeholderItems}
                  isAttachmentsDirty={isAttachmentsDirty}
                  isNewSelection={isNewSelection}
                  isUpdating={updating}
                  onReorder={handleReorder}
                  onRemove={(mediaId) => {
                    const mediaItem = allMedia.find((m) => m.id === mediaId)
                    if (mediaItem) handleSelect(mediaId, mediaItem, false)
                  }}
                  onSave={handleSave}
                />
              )}
            </div>
          ) : (
            <MediaGridEmptyState
              className="px-4 py-3"
              uploadInputId={uploadInputId}
              isUploading={isUploading}
              uploadError={uploadError}
              onFileUpload={handleFileUpload}
              allowedMediaKinds={mediaManagerContext.allowedMediaKinds}
            />
          )}
        </Drawer.Content>
      </Drawer>
    </>
  )
}
